"use client";

import React from "react";
import { ShieldAlert, ShieldCheck, House, ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inclusions = [{
  title: "Interior & Exterior Defense",
  description: "Complete protection starting from the inside out. We target nesting areas, entry points, and high-traffic pest zones within your home, followed by a robust exterior barrier to stop invaders before they ever cross your threshold.",
  icon: House,
  theme: "dark",
  features: ["Baseboard & Crevice Treatment", "Exterior Perimeter Spray", "Entry Point Sealing Advice"]
}, {
  title: "Rural Perimeter Protection",
  description: "Specialized, heavy-duty protection designed for ranches, estates, and large properties in Punta Gorda and surrounding areas. We establish a wide, unyielding defensive perimeter to keep encroaching wildlife and heavy pest populations at bay.",
  icon: ShieldAlert,
  theme: "yellow",
  features: ["Extended Property Line Barrier", "Mosquito & Tick Reduction", "Agricultural Pest Assessment"]
}, {
  title: "Free Re-Treatment Guarantee",
  description: "Your peace of mind is our absolute priority. If covered pests return between your regularly scheduled treatments, we will return to re-treat the affected areas at absolutely no additional cost to you. No questions asked.",
  icon: ShieldCheck,
  theme: "dark",
  features: ["100% Satisfaction Focus", "Rapid Response Times", "Zero Hidden Fees"]
}];
export default function ServiceInclusions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="serviceinclusions" className="bg-card py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12">
        
        {}
        <div ref={headerRef} className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Badge variant="outline" className="mb-6 border-primary text-primary font-default uppercase tracking-widest text-sm py-1.5 px-4 rounded-none">
            Service Scope
          </Badge>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground uppercase tracking-tight mb-6">
            Unyielding Protection For <br className="hidden md:block" />
            <span className="text-primary">House & Ranch</span>
          </h2>
          <Separator className="w-24 h-1 bg-primary mx-auto mb-8" />
          <p className="font-serif text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            At Peace River Pest Control, we don't just spray and pray. Our comprehensive service inclusions are designed to eradicate current infestations and fortify your property against future invasions.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-0 shadow-2xl transition-all duration-1000 delay-200 transform ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {inclusions.map((item, i) => {
          const isDark = item.theme === "dark";
          return <div key={i} data-index={i} className="lg:col-span-4 flex">
                <Card className={`w-full rounded-none border-0 flex flex-col ${isDark ? "bg-primary text-primary-foreground" : "bg-background text-foreground"}`}>
                  <CardHeader className="pb-4 pt-10 px-8 md:px-10">
                    <div className={`mb-6 inline-flex p-4 rounded-none ${isDark ? "bg-primary-foreground/10" : "bg-primary/10"}`}>
                      <item.icon className={`w-10 h-10 ${isDark ? "text-primary-foreground" : "text-primary"}`} />
                    </div>
                    <CardTitle className="font-default text-2xl md:text-3xl font-bold uppercase tracking-wide leading-tight">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-8 md:px-10 pb-10 flex-grow flex flex-col">
                    <p className={`font-serif text-lg leading-relaxed mb-8 flex-grow ${isDark ? "text-primary-foreground/90" : "text-foreground/80"}`}>
                      {item.description}
                    </p>
                    
                    <div className="space-y-3 mt-auto">
                      {item.features.map((feature, fIndex) => <div key={fIndex} data-index={fIndex} className="flex items-start gap-3">
                          <CircleCheck className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-background" : "text-primary"}`} />
                          <span className={`font-serif text-base ${isDark ? "text-primary-foreground/80" : "text-foreground/70"}`}>
                            {feature}
                          </span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div className="mt-20 text-center">
          <Button asChild size="lg" className="rounded-none font-default uppercase tracking-wider text-lg px-10 py-8 shadow-xl hover:scale-105 transition-transform duration-300">
            <Link href="/ContactPage">
              Schedule Your Free Inspection
              <ArrowRight className="ml-2 w-6 h-6" />
            </Link>
          </Button>
          <p className="font-serif text-muted-foreground mt-6 text-lg">
            Serving Punta Gorda, Port Charlotte, Cape Coral, and surrounding areas.
          </p>
        </div>

      </div>
    </section>;
}